import { type CheckoutDraft, saveCheckoutDraft } from "@/lib/billing/checkout-draft";
import { isEmbeddedCheckoutEnabled } from "@/lib/billing/checkout-mode";

export type BeginProCheckoutResult =
	| { mode: "embedded" }
	| { mode: "hosted"; redirected: true }
	| { mode: "hosted"; error: string; code?: string };

type BeginProCheckoutParams = {
	draft: CheckoutDraft;
	/** Guest email — omit when the user is already signed in. */
	email?: string;
	router: { push: (href: string) => void };
};

export async function beginProCheckout({
	draft,
	email,
	router,
}: BeginProCheckoutParams): Promise<BeginProCheckoutResult> {
	if (isEmbeddedCheckoutEnabled()) {
		saveCheckoutDraft({
			...draft,
			...(email ? { email: email.trim().toLowerCase() } : {}),
		});
		router.push("/checkout");
		return { mode: "embedded" };
	}

	const checkoutRes = await fetch("/api/billing/checkout-session", {
		method: "POST",
		credentials: "include",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			offerId: draft.offerId,
			trialAccepted: draft.trialAccepted,
			yearlyAccepted: draft.yearlyAccepted,
			...(email ? { email: email.trim().toLowerCase() } : {}),
			...(draft.client === "ios" ? { client: "ios" } : {}),
		}),
	});

	const payload = (await checkoutRes.json().catch(() => ({}))) as {
		url?: string;
		error?: string;
		code?: string;
	};

	if (!checkoutRes.ok) {
		return {
			mode: "hosted",
			error: payload.error || "Could not start checkout.",
			code: payload.code,
		};
	}

	if (!payload.url) {
		return { mode: "hosted", error: "Checkout URL missing." };
	}

	window.location.href = payload.url;
	return { mode: "hosted", redirected: true };
}
